import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { validateEmail } from "../utils/validation";

export interface ContactDetails {
  fullName: string;
  email: string;
}

interface Props {
  orderId?: number | string | null;
  onClose: () => void;
  onSubmit: (details: ContactDetails, orderId?: number | string | null) => Promise<void> | void;
}

export default function ContactDetailsModal({ orderId, onClose, onSubmit }: Props) {
  const id = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const backdropPressed = useRef(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({ fullName: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    nameRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== "Tab") return;
      const elements = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), [tabindex="0"]',
      );
      if (!elements?.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus({ preventScroll: true });
    };
  }, []);

  const inputClass =
    "tw-h-12 tw-w-full tw-rounded-button tw-border tw-bg-surface tw-px-4 tw-text-base tw-leading-[22px] tw-text-text-primary tw-outline-none tw-transition-colors placeholder:tw-text-text-muted hover:tw-border-text-primary focus:tw-border-primary focus-visible:tw-ring-2 focus-visible:tw-ring-primary";

  return createPortal(
    <div
      className="auth-modal-overlay contact-details-overlay"
      onMouseDown={(event) => {
        backdropPressed.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (backdropPressed.current && event.target === event.currentTarget)
          onClose();
        backdropPressed.current = false;
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        className="tw-relative tw-w-full tw-max-w-[464px] tw-rounded-panel tw-bg-surface tw-p-6 tw-text-left tw-font-base tw-text-text-primary tw-shadow-[0_0_16px_rgba(40,41,61,0.1)] md:tw-p-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="tw-absolute tw-right-3 tw-top-3 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-control tw-text-text-muted hover:tw-bg-surface-muted hover:tw-text-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-secondary"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
        <h2
          id={`${id}-title`}
          className="tw-pr-9 tw-text-2xl tw-font-semibold tw-leading-[29px]"
        >
          Укажите ФИО и email
        </h2>
        <p
          id={`${id}-description`}
          className="tw-mt-3 tw-text-base tw-leading-[22px] tw-text-text-secondary"
        >
          Чтобы мы знали, как к вам обращаться, и могли сообщать о статусе
          заказа.
        </p>
        <form
          className="tw-mt-6"
          noValidate
          onSubmit={async (event) => {
            event.preventDefault();
            const name = fullName.trim().replace(/\s+/g, " ");
            const normalizedEmail = email.trim();
            const emailValidation = validateEmail(normalizedEmail);
            const nextErrors = {
              fullName:
                name.split(" ").length < 2
                  ? "Укажите фамилию и имя. Отчество — если есть."
                  : "",
              email: emailValidation.valid
                ? ""
                : "Укажите корректный email, например name@mail.ru.",
            };
            setErrors(nextErrors);
            if (nextErrors.fullName) return nameRef.current?.focus();
            if (nextErrors.email) return emailRef.current?.focus();

            try {
              setIsSubmitting(true);
              setSubmitError("");
              await onSubmit({ fullName: name, email: normalizedEmail }, orderId);
            } catch (err: any) {
              setSubmitError(err?.message || "Не удалось сохранить контактные данные");
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          <label
            htmlFor={`${id}-name`}
            className="tw-mb-2 tw-block tw-text-sm tw-font-medium"
          >
            ФИО
          </label>
          <input
            ref={nameRef}
            id={`${id}-name`}
            name="name"
            autoComplete="name"
            required
            disabled={isSubmitting}
            maxLength={200}
            placeholder="Фамилия Имя Отчество"
            value={fullName}
            onChange={(event) => {
              setFullName(event.target.value);
              setErrors((current) => ({ ...current, fullName: "" }));
              setSubmitError("");
            }}
            aria-invalid={Boolean(errors.fullName)}
            aria-describedby={errors.fullName ? `${id}-name-error` : undefined}
            className={`${inputClass} ${errors.fullName ? "tw-border-danger" : "tw-border-border-muted"}`}
          />
          {errors.fullName && (
            <p
              id={`${id}-name-error`}
              role="alert"
              className="tw-mt-2 tw-text-sm tw-text-danger"
            >
              {errors.fullName}
            </p>
          )}
          <label
            htmlFor={`${id}-email`}
            className="tw-mb-2 tw-mt-5 tw-block tw-text-sm tw-font-medium"
          >
            Email
          </label>
          <input
            ref={emailRef}
            id={`${id}-email`}
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            disabled={isSubmitting}
            maxLength={254}
            placeholder="name@mail.ru"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((current) => ({ ...current, email: "" }));
              setSubmitError("");
            }}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={`${id}-email-hint${errors.email ? ` ${id}-email-error` : ""}`}
            className={`${inputClass} ${errors.email ? "tw-border-danger" : "tw-border-border-muted"}`}
          />
          {errors.email && (
            <p
              id={`${id}-email-error`}
              role="alert"
              className="tw-mt-2 tw-text-sm tw-text-danger"
            >
              {errors.email}
            </p>
          )}
          {submitError && (
            <p
              role="alert"
              className="tw-mt-3 tw-text-sm tw-font-medium tw-text-danger"
            >
              {submitError}
            </p>
          )}
          <p
            id={`${id}-email-hint`}
            className="tw-mt-2 tw-text-sm tw-leading-5 tw-text-text-secondary"
          >
            Будем присылать уведомления о вашем заказе. Без рекламных рассылок.
          </p>
          <button
            type="submit"
            disabled={isSubmitting}
            className="tw-mt-6 tw-h-[52px] tw-w-full tw-rounded-button tw-bg-primary tw-px-4 tw-text-lg tw-font-medium tw-text-text-inverse tw-transition-colors hover:tw-bg-primary-hover active:tw-bg-primary-active focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-secondary focus-visible:tw-ring-offset-2 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
          >
            {isSubmitting ? "Отправка..." : "Отправить"}
          </button>
        </form>
      </section>
    </div>,
    document.body,
  );
}
